import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { ICartInput } from "@types";
import { useLayoutContext } from "@context/layout";
import { toFarsiNumber } from "@utils/digits";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import howToLearnApi from "../helpers/howToLearnApi";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { HOW_TO_LEARN_PAGE_LIMIT } from "../helpers/constants";

const useInfiniteScroll = () => {
  const router = useRouter();
  const { isAdmin } = useLayoutContext();
  const isMobile = useIsMobile(640);

  const observer = useRef<IntersectionObserver | null>(null);
  const productRef = useRef<HTMLDivElement>(null);
  const [lastElement, setLastElement] = useState<HTMLDivElement | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const { data: landingDetail } = useRequestImmutable(
    howToLearnApi.singleHowToLearn(String(router.query.slug)),
    { shouldRetryOnError: false }
  );
  const landingId = landingDetail?.data?.id;
  const { data } = useRequestImmutable(
    landingId
      ? howToLearnApi.singleHowToLearnProductV2(String(landingId), {
          page: pageNum,
          limit: HOW_TO_LEARN_PAGE_LIMIT,
        })
      : undefined,
    { revalidateOnReconnect: true, shouldRetryOnError: false }
  );

  const [collection, setCollection] = useState<ICartInput[]>(
    data?.products?.length ? data.products : []
  );
  const [loading, setLoading] = useState(true);
  const [paginationMode, setPaginationMode] = useState(false);

  useEffect(() => {
    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setLoading(true);

        const hasMore = data?.pagination?.remaining_pages !== 0;
        if (hasMore && !paginationMode) {
          setPageNum((prev) => prev + 1);
        }
      }
    });
  }, [data?.pagination?.remaining_pages, paginationMode]);

  useEffect(() => {
    const currentElement = lastElement;
    const currentObserver = observer.current;

    if (currentElement && currentObserver) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement && currentObserver) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [lastElement]);
  const fetchData = async () => {
    if (!data) return;

    setLoading(false);

    const { products = [], pagination } = data;

    setCollection((prev) => (pageNum > 1 ? [...prev, ...products] : products));

    const totalPages =
      typeof pagination?.total_pages === "number"
        ? pagination.total_pages
        : Math.ceil(
            Number(pagination?.total_items ?? 0) / HOW_TO_LEARN_PAGE_LIMIT
          );

    setTotalPage(totalPages);

    if (!paginationMode || !productRef.current) return;

    const offsetTop = isAdmin ? 160 : 80;
    const { top: bodyTop } = document.body.getBoundingClientRect();
    const { top: elementTop } = productRef.current.getBoundingClientRect();

    window.scrollTo({
      top: elementTop - bodyTop - offsetTop,
      behavior: "smooth",
    });
  };
  useEffect(() => {
    fetchData();
  }, [data, pageNum, paginationMode, isAdmin]);

  useEffect(() => {
    setPageNum(1);
    setPaginationMode(false);
    setTotalPage(1);
  }, [router.query.slug]);

  const handleClickOnPage = (page: number) => {
    setLoading(true);
    setPageNum(page);
    setCollection([]);
    setPaginationMode(true);
  };

  const productNumber = (collectinIndex: number) =>
    toFarsiNumber(
      String(
        !paginationMode
          ? collectinIndex + 1
          : (pageNum - 1) * HOW_TO_LEARN_PAGE_LIMIT + collectinIndex + 1
      )
    );
  return {
    productRef,
    collection,
    pageNum,
    paginationMode,
    loading,
    totalPage,
    setLastElement,
    data,
    isMobile,
    handleClickOnPage,
    productNumber,
  };
};

export default useInfiniteScroll;
